import { electronModule } from "@/modules/electron";
import { getDesktopAudioCaptureDiagnosticsContext } from "@/modules/electron/desktop-audio-capture-diagnostics";
import { getDesktopAudioArtifactDurationMs } from "@/modules/electron/desktop-audio-artifact-ref";
import { logger } from "@/modules/logger";
import {
  DesktopAudioArtifactRemoveReason,
  DesktopAudioArtifactState,
} from "@/modules/electron/types";
import {
  AppendAudioChunkArgs,
  AudioRecordingArtifactDebugSummary,
  AudioRecordingArtifactState,
  AudioRecordingStrategyDriver,
  CompleteRecordingArgs,
  EnsureLocalRecordingStartedArgs,
  GetArtifactDebugSummaryArgs,
  GetInitialAudioChunkCountArgs,
  GetPersistedDurationMsArgs,
  GetUploadableArtifactStatusArgs,
  RemoveLocalArtifactsArgs,
  UploadableAudioRecordingArtifactStatus,
} from "./audio-recording-strategy-driver";

/**
 * Renderer-side strategy driver for desktop-owned artifacts.
 *
 * The renderer does not append desktop audio chunks itself. Electron owns the
 * live capture session and artifact writes, so this driver only handles the
 * durable operations the renderer still needs: finalize, inspect, and remove.
 */
export class DesktopArtifactRecordingStrategyDriver implements AudioRecordingStrategyDriver {
  async ensureLocalRecordingStarted(_args: EnsureLocalRecordingStartedArgs): Promise<void> {}

  async getInitialAudioChunkCount(_args: GetInitialAudioChunkCountArgs): Promise<number> {
    return 0;
  }

  async appendAudioChunk(_args: AppendAudioChunkArgs): Promise<void> {}

  async completeRecording({ audioRecordingId }: CompleteRecordingArgs): Promise<number> {
    const finalizedArtifact = await electronModule.audioArtifacts.finalizeAudioArtifact({
      audioRecordingId,
    });

    if (!finalizedArtifact) {
      logger.error({
        message: "[DesktopArtifactRecordingStrategyDriver] Failed to finalize desktop artifact",
        info: {
          audioRecordingId,
          hasFinalizedArtifact: false,
        },
      });

      throw new Error(`Failed to finalize desktop artifact recording ${audioRecordingId}`);
    }

    const durationMs = getDesktopAudioArtifactDurationMs({
      artifact: finalizedArtifact,
    });

    if (durationMs === null) {
      logger.error({
        message:
          "[DesktopArtifactRecordingStrategyDriver] Finalized desktop artifact is missing persisted audio data",
        info: {
          audioRecordingId,
          artifactSizeBytes: finalizedArtifact.sizeBytes,
          artifactDurationMs: finalizedArtifact.durationMs ?? null,
          artifactMimeType: finalizedArtifact.mimeType,
          artifactMetadataKeyCount: finalizedArtifact.metadata
            ? Object.keys(finalizedArtifact.metadata).length
            : 0,
        },
      });

      throw new Error(
        `Completed desktop artifact recording ${audioRecordingId} is missing persisted audio data`
      );
    }

    return durationMs;
  }

  async getPersistedDurationMs({ audioRecordingId }: GetPersistedDurationMsArgs) {
    const artifactSnapshot = await electronModule.audioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    });

    return getDesktopAudioArtifactDurationMs({
      artifact: artifactSnapshot,
    });
  }

  async getArtifactDebugSummary({
    audioRecordingId,
  }: GetArtifactDebugSummaryArgs): Promise<AudioRecordingArtifactDebugSummary> {
    const snapshot = await electronModule.audioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    });
    const captureDiagnosticsContext = getDesktopAudioCaptureDiagnosticsContext({
      metadata: snapshot?.metadata,
    });

    return {
      durationMs:
        getDesktopAudioArtifactDurationMs({
          artifact: snapshot,
        }) ?? 0,
      partCount: snapshot ? 1 : 0,
      sizeBytes: snapshot?.sizeBytes ?? 0,
      state: snapshot?.state
        ? snapshot.state === DesktopAudioArtifactState.Finalized
          ? AudioRecordingArtifactState.Finalized
          : AudioRecordingArtifactState.Writing
        : undefined,
      filePath: snapshot?.filePath,
      appendCount: snapshot?.appendCount,
      bytesWritten: snapshot?.bytesWritten,
      createdAt: snapshot?.createdAt,
      updatedAt: snapshot?.updatedAt,
      firstAppendAt: snapshot?.firstAppendAt,
      lastAppendAt: snapshot?.lastAppendAt,
      finalizedAt: snapshot?.finalizedAt,
      ...captureDiagnosticsContext,
    };
  }

  async getUploadableArtifactStatus({
    audioRecordingId,
  }: GetUploadableArtifactStatusArgs): Promise<UploadableAudioRecordingArtifactStatus> {
    const artifactRef = await electronModule.audioArtifacts.getAudioArtifactRef({
      audioRecordingId,
    });

    return {
      artifactSizeBytes: artifactRef?.sizeBytes ?? null,
      hasUploadableArtifact: artifactRef !== null && artifactRef.sizeBytes > 0,
    };
  }

  async removeLocalArtifacts({ audioRecordingId }: RemoveLocalArtifactsArgs) {
    await electronModule.audioArtifacts.removeAudioArtifact({
      audioRecordingId,
      removeReason: DesktopAudioArtifactRemoveReason.LocalArtifactsCleared,
    });
  }
}
